import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Custom Components
import Loading from "components/loading/loading.component";
import Button from "components/form/buttons/button/button.component";
import dialog from "helpers/dialog/dialog.helper";
import notification from "helpers/notification/notification.helper";
import * as baseAction from "store/base/base.action";
import * as customersAction from "store/customers/customers.action";

// Styles
import "./customer-databases.styles.scss";

const CustomerDatabases = () => {
  const dispatch = useDispatch();

  const customerProfile = useSelector(
    state => state.customers[customersAction.GET_CUSTOMER]
  );
  const customerDatabases = useSelector(
    state => state.customers[customersAction.GET_CUSTOMER_DATABASES]
  );
  const isLoading = useSelector(
    state => state.customers[customersAction.CUSTOMER_DATABASES_LOADING]
  );
  const isProvisioning = useSelector(
    state => state.customers[customersAction.PROVISION_DATABASES_LOADING]
  );
  const provisionResponse = useSelector(
    state => state.customers[customersAction.PROVISION_DATABASES]
  );
  const provisionErrors = useSelector(
    state => state.customers[customersAction.PROVISION_DATABASES_ERRORS]
  );
  const databasesErrors = useSelector(
    state => state.customers[customersAction.CUSTOMER_DATABASES_ERRORS]
  );

  const databases = customerDatabases?.databases || [];
  const missingRepairable = databases.filter(
    database => !database.is_registered && database.is_repairable
  );
  const missingManual = databases.filter(
    database => !database.is_registered && !database.is_repairable
  );

  // Fetch the per service database status
  useEffect(() => {
    if (customerProfile?.id) {
      dispatch(customersAction.getCustomerDatabases(customerProfile.id));
    }
  }, [customerProfile, dispatch]);

  // Creation is queued on a listener, so the registry only catches up a moment
  // later - tell the user rather than silently showing stale rows.
  useEffect(() => {
    if (provisionResponse?.notifications) {
      notification.success(provisionResponse.notifications, {
        position: "top-left"
      });
      dispatch(baseAction.reset(customersAction.PROVISION_DATABASES));
    }
    // eslint-disable-next-line
  }, [provisionResponse, dispatch]);

  useEffect(() => {
    if (provisionErrors) {
      notification.error(provisionErrors, { position: "top-left" });
      dispatch(baseAction.reset(customersAction.PROVISION_DATABASES_ERRORS));
    }
    // eslint-disable-next-line
  }, [provisionErrors, dispatch]);

  useEffect(() => {
    if (databasesErrors) {
      notification.error(databasesErrors, { position: "top-left" });
      dispatch(baseAction.reset(customersAction.CUSTOMER_DATABASES_ERRORS));
    }
    // eslint-disable-next-line
  }, [databasesErrors, dispatch]);

  const handleRefresh = () => {
    dispatch(customersAction.getCustomerDatabases(customerProfile.id));
  };

  const handleProvision = () => {
    const services = missingRepairable.map(database => database.service);
    dialog
      .confirm(
        `Create the missing database(s) for ${services.join(
          ", "
        )}? The customer's existing data is not touched.`,
        function () {
          dispatch(
            customersAction.provisionCustomerDatabases(
              customerProfile.id,
              services
            )
          );
        }
      )
      .set("labels", { ok: "Yes", cancel: "No" })
      .setHeader("Confirmation");
  };

  return (
    <div className="app__customer-databases--page">
      <div className="app__user--profile-form-heading-container">
        <h4 className="app__user--profile-form-heading">Databases</h4>
        <Button
          id="refresh-databases"
          className="btn--outline-dark"
          icon="fas fa-sync"
          isLoading={isLoading}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </div>

      <div className="app__user--profile-form">
        <div className="hr mt-20"></div>

        <Loading isLoading={isLoading} />

        {!isLoading && (
          <>
            <table className="table mt-20">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Database</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {databases.map(database => (
                  <tr key={database.service}>
                    <td>{database.service_name}</td>
                    <td className="text-muted">
                      {database.db_name || database.expected_db_name}
                    </td>
                    <td>
                      {database.is_registered ? (
                        <span className="app__customer-databases--registered">
                          <i className="fas fa-check-circle mr-10"></i>
                          Registered
                        </span>
                      ) : (
                        <span className="app__customer-databases--missing">
                          <i className="fas fa-exclamation-circle mr-10"></i>
                          {database.lookup_error
                            ? "Lookup failed"
                            : "Not registered"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {missingManual.length > 0 && (
              <div className="app__customer-databases--note mt-20">
                {missingManual.map(database => database.service_name).join(", ")}{" "}
                cannot be created from here - provisioning them re-seeds the
                tenant's administrator and settings. Repair them manually.
              </div>
            )}

            <div className="hr my-30"></div>

            {missingRepairable.length > 0 ? (
              <Button
                id="provision-databases"
                className="btn--info"
                icon="fas fa-database"
                isLoading={isProvisioning}
                onClick={handleProvision}
              >
                Provision missing database(s)
              </Button>
            ) : (
              <div className="text-muted">
                Every tenant database is registered.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerDatabases;
